import { Box, Button, CircularProgress, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useCallback, useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import EditAndApplyTemplateDialog from './EditAndApplyTemplateDialog'
import EditTemplateDialog from './EditTemplateDialog'
import PreviewTemplateDialog from './PreviewTemplateDialog'
import PreviewCurrentLayoutDialog from './PreviewCurrentLayoutDialog'
import CreateTemplateDialog from './CreateTemplateDialog'

/**
 * Widget template catalog for a device (asset), opened from the Assets table row
 * option "Change widget template". Covers the catalog management within device
 * configuration (HU-01): create a new template, save the device's current layout
 * as a template (HU-04, from-device), edit a template (PUT) and delete it; plus
 * assigning to the device: preview, apply (with overwrite confirmation — HU-02/
 * HU-03) and "edit and apply" (edit a copy and save it to the device).
 */
const ApplyWidgetTemplateDialog = ({ open, setOpen, device, onApplied }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const [AlertElement, showAlert] = AlertUI()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  // { type: 'apply' | 'delete', template } — drives the confirmation dialog.
  const [pendingAction, setPendingAction] = useState(null)
  const [editorTemplate, setEditorTemplate] = useState(null)
  const [templateToEdit, setTemplateToEdit] = useState(null)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [previewCurrentOpen, setPreviewCurrentOpen] = useState(false)
  // Per-row overflow menu (preview / edit template / delete).
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const [menuTemplate, setMenuTemplate] = useState(null)

  const openRowMenu = (event, template) => {
    setMenuAnchorEl(event.currentTarget)
    setMenuTemplate(template)
  }
  const closeRowMenu = () => {
    setMenuAnchorEl(null)
    setMenuTemplate(null)
  }

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const result = await apiGet('widgets/templates')
      setTemplates(Array.isArray(result) ? result : [])
    } catch (e) {
      showAlert({ title: t_('error loading widget templates'), message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [showAlert, t_])

  useEffect(() => {
    if (open) loadTemplates()
  }, [open, loadTemplates])

  const doApply = useCallback(async (template) => {
    if (!template || !device) return
    setBusy(true)
    try {
      await apiPost('widgets/templates/' + template.id + '/apply/' + device.id, {})
      if (onApplied) onApplied()
      setOpen(false)
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [device, onApplied, setOpen, showAlert, t_])

  const doDelete = useCallback(async (template) => {
    if (!template) return
    setBusy(true)
    try {
      await apiDelete('widgets/templates/' + template.id)
      showAlert({ title: t_('Template deleted'), message: '', severity: 'success' })
      await loadTemplates()
    } catch (e) {
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [loadTemplates, showAlert, t_])

  const requestApply = useCallback(async (template) => {
    if (!device) return
    try {
      const current = await apiGet('widgets/device/' + device.id)
      if (Array.isArray(current) && current.length > 0) {
        setPendingAction({ type: 'apply', template })
        return
      }
    } catch (e) {
      // If the current layout cannot be read, fall through and apply anyway.
    }
    doApply(template)
  }, [device, doApply])

  const confirmPending = () => {
    const action = pendingAction
    setPendingAction(null)
    if (!action) return
    if (action.type === 'apply') doApply(action.template)
    else doDelete(action.template)
  }

  const confirmMessage = pendingAction?.type === 'delete'
    ? t_('This template will be permanently deleted. Continue?')
    : t_('Applying this template will overwrite the current layout of this device. Continue?')

  return (
    <>
      <SmartDialog
        setOpen={open}
        closeCallback={() => setOpen(false)}
        close
        accept={false}
        cancel={false}
        paperWidth='min(72rem, 92vw)'
        title={{ icon: null, render: <Typography variant='h5'>{t_('Change widget template')}{device ? ' — ' + device.name : ''}</Typography> }}
        renderComponent={
          <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
            {AlertElement}
            <Stack direction='row' justifyContent='flex-end' spacing={1} sx={{ mb: 1 }}>
              <Button variant='text' startIcon={<VisibilityIcon />} onClick={() => setPreviewCurrentOpen(true)} disabled={busy || !device}>
                {t_('Preview current layout')}
              </Button>
              <Button variant='outlined' startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} disabled={busy}>
                {t_('Create new template')}
              </Button>
            </Stack>
            {loading
              ? <Stack alignItems='center' sx={{ py: 3 }}><CircularProgress /></Stack>
              : templates.length === 0
                ? <Typography color='text.secondary' sx={{ py: 2, textAlign: 'center' }}>{t_('No templates yet')}</Typography>
                : (
                  <Stack divider={<Divider flexItem />} spacing={0}>
                    {templates.map((template) => (
                      <Stack key={template.id} direction='row' spacing={0.5} alignItems='center' sx={{ py: 1 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography noWrap>{template.name}</Typography>
                          {template.updatedAt && (
                            <Typography variant='caption' color='text.secondary'>{formatDateTime(template.updatedAt)}</Typography>
                          )}
                        </Box>
                        <Button size='small' variant='text' onClick={() => setEditorTemplate(template)} disabled={busy}>
                          {t_('Edit and apply')}
                        </Button>
                        <Button size='small' variant='outlined' startIcon={<PlaylistAddCheckIcon />} onClick={() => requestApply(template)} disabled={busy}>
                          {t_('Apply')}
                        </Button>
                        <IconButton size='small' onClick={(event) => openRowMenu(event, template)} disabled={busy} aria-label={t_('More options')}>
                          <MoreVertIcon fontSize='small' />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                  )}
            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeRowMenu}>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setPreviewTemplate(template) }}>
                <ListItemIcon><VisibilityIcon fontSize='small' /></ListItemIcon>
                <ListItemText>{t_('Preview')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setTemplateToEdit(template) }}>
                <ListItemIcon><EditIcon fontSize='small' /></ListItemIcon>
                <ListItemText>{t_('Edit template')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { const template = menuTemplate; closeRowMenu(); setPendingAction({ type: 'delete', template }) }}>
                <ListItemIcon><DeleteOutlineIcon fontSize='small' color='error' /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>{t_('Delete')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        }
      />

      <SmartDialog
        setOpen={Boolean(pendingAction)}
        title={createDialogTitle({ color: 'warning', title: t_('Attention'), icon: WarningAmberIcon })}
        message={confirmMessage}
        accept
        acceptLabel={t_('Continue')}
        acceptCallback={confirmPending}
        cancel
        cancelCallback={() => setPendingAction(null)}
      />

      <EditAndApplyTemplateDialog
        open={Boolean(editorTemplate)}
        template={editorTemplate}
        device={device}
        onClose={(saved) => {
          setEditorTemplate(null)
          if (saved) {
            if (onApplied) onApplied()
            setOpen(false)
          }
        }}
      />

      <EditTemplateDialog
        open={Boolean(templateToEdit)}
        template={templateToEdit}
        onClose={(saved) => {
          setTemplateToEdit(null)
          if (saved) loadTemplates()
        }}
      />

      <PreviewTemplateDialog
        open={Boolean(previewTemplate)}
        template={previewTemplate}
        device={device}
        onClose={() => setPreviewTemplate(null)}
        onApply={(template) => { setPreviewTemplate(null); requestApply(template) }}
        onEdit={(template) => { setPreviewTemplate(null); setEditorTemplate(template) }}
      />

      <CreateTemplateDialog
        open={createOpen}
        device={device}
        onClose={(outcome) => {
          setCreateOpen(false)
          if (outcome === 'applied') {
            if (onApplied) onApplied()
            setOpen(false)
          } else if (outcome === 'saved') {
            loadTemplates()
          }
        }}
      />

      <PreviewCurrentLayoutDialog
        open={previewCurrentOpen}
        device={device}
        onClose={() => setPreviewCurrentOpen(false)}
        onSaved={loadTemplates}
      />
    </>
  )
}

export default ApplyWidgetTemplateDialog
